// 🏥 PROMPT PARA ANÁLISE IA DE EXAMES DE IMAGEM
// Sistema: HEPATO TX RONDÔNIA
// Arquivo: src/modules/ia-analise/services/gerarPromptExameImagem.ts
// VERSÃO 5 - Equilíbrio de destaque CHC vs TVP + correções anteriores

import { Paciente } from "../../../types";
import {
  REGRA_TUMORES_MULTIPLOS_IMAGEM,
  REGRA_LIRADS_IMAGEM,
  REGRA_ESCOPO_SETORIAL,
} from "./regrasClinicas";
import { ehDoadorFigado } from "../../../shared/utils/modalidadeAtendimento";
import { calcularIMC } from "../../../shared/utils/volumetriaDoador";
import {
  detectarCategoriaExame,
  getTemplateEspecifico,
  getTemplateDoadorFigado,
} from "./templatesExames";
import {
  detectarCenarioClinico,
  gerarBlocoIndicacaoClinica,
  validarCriterioRiscoCHC,
} from "./enquadramentoClinico";

// B305 (AI_138) — o modelo vinha fechando a análise de imagem com um rodapé de
// META-COMENTÁRIO ("Observações de segurança da análise": nº de transplantes
// considerado, escores não calculados, dados que "não constam"). É ruído para o
// médico (pedido de Leonardo). Instrução escopada à análise de imagem (não à
// Análise Geral). Injetada no fim dos DOIS templates (especializado e padrão).
const REGRA_SEM_META_FOOTER = `
### 🚫 REGRA DE APRESENTAÇÃO — SEM RODAPÉ DE META-COMENTÁRIO
NÃO acrescente ao final um bloco de "Observações de segurança", "Notas da análise" ou meta-comentário sobre o PROCESSAMENTO — por exemplo: quantos transplantes foram considerados, que escores (MELD/Child) não foram calculados, ou quais dados "não constam e devem ser recuperados". Isso é ruído para o médico e NÃO deve aparecer. Caveats CLÍNICOS pertinentes ao exame (ex.: "correlacionar com RM contrastada", "AFP não disponível") vão nas Recomendações, de forma objetiva — nunca como auto-auditoria do sistema.`;

// B305 (AI_138) — a análise de imagem estava recomendando conduta TERAPÊUTICA
// (ex.: conversão para inibidor de mTOR, ajuste de imunossupressão) sobre um dado
// que ela mesma reconhecia AUSENTE ("regime atual não consta"). Conduta sobre dado
// que não está no exame nem nos dados é especulação. Filosofia "rotular o dado, não
// empilhar regra": quando o dado necessário falta, SINALIZAR a lacuna — não presumir
// e recomendar a mudança. Escopada à análise de imagem (a Análise Geral, com o quadro
// completo, é quem conduz o manejo).
const REGRA_CONDUTA_SOBRE_DADO_AUSENTE = `
### 🩺 REGRA — NÃO RECOMENDAR CONDUTA SOBRE DADO AUSENTE
NÃO recomende INÍCIO, TROCA ou AJUSTE de terapia (imunossupressão, esquema oncológico, doses/metas) apoiado em um dado que NÃO consta nos dados fornecidos. Se um dado necessário para a conduta está AUSENTE (ex.: esquema imunossupressor atual, AFP, função do enxerto), apenas SINALIZE a lacuna de forma objetiva ("o esquema imunossupressor atual não consta — recuperar antes de qualquer decisão"), SEM propor a mudança específica. Você PODE indicar QUE a decisão terapêutica cabe à equipe/tumor board; NÃO antecipe QUAL conduta (ex.: não recomende "converter para mTOR" nem cite metas de nível sérico) quando o esquema atual é desconhecido. Regras clínicas gerais (ex.: alvo de tacrolimo em esquema com mTOR) só se aplicam quando o esquema está DOCUMENTADO nos dados.`;

// B305/AI_138 — DIVERGÊNCIA CAMPO×DADOS (decisão clínica de Leonardo, validada num caso
// real: ficha de doador marcava "Infecção: Não", mas havia leucocitose de 34.810 + dois
// antibióticos). O modelo afirmou "Infecção: Sim" como se fosse o valor da ficha — deturpou
// o campo. O certo NÃO é forçar o texto a bater com o checkbox (o campo pode estar errado/
// incompleto), e sim EXPLICITAR os dois lados. Preserva o juízo clínico E a fonte.
const REGRA_DIVERGENCIA_CAMPO_DADOS = `
### 🔀 REGRA — QUANDO UM CAMPO/RÓTULO DIVERGIR DOS DADOS, MOSTRE OS DOIS LADOS
Se um campo estruturado, checkbox ou afirmação explícita do laudo/ficha CONTRARIAR os dados clínicos apresentados, NÃO afirme silenciosamente o oposto do campo (não escreva o valor que você INFERE como se fosse o que o documento registra). EXPLICITE a divergência, nomeando AMBOS: o que o documento marca E o que os dados sugerem. Ex.: "Apesar de a ficha marcar Infecção: NÃO, o doador apresenta leucocitose de 34.810 e antibioticoterapia (cefazolina/ceftriaxona), o que levanta a possibilidade de processo infeccioso não assinalado; hemocultura negativa e sem germe isolado." Preserve o seu juízo clínico — o objetivo é tornar a divergência VISÍVEL para o médico, não suprimi-la nem forçar o texto a concordar com o campo. Vale para qualquer conflito campo×dado (ex.: laudo "sem varizes" mas descreve uma; valor fora da faixa rotulado "normal").`;

// ✅ Interface atualizada com achados estruturados
interface AchadosExame {
  tipo?: string;
  data?: string;
  lesoesHepaticas?: string;
  statusMilao?: "DENTRO" | "FORA" | "NÃO APLICÁVEL" | "";
  trombosePortal?: string;
  tipoTrombose?: "BLAND" | "TUMORAL" | "NÃO APLICÁVEL" | "";
  shunts?: string;
  ascite?: "SIM" | "NÃO" | "";
  esplenomegalia?: string;
  outrosAchados?: string;
  conclusao?: string;
  // ✅ Bloco 1 AI_34 — transcrição literal do laudista (Haiku vision)
  conclusaoLaudistaLiteral?: string;
  tipoLaudista?:
    | "patologista"
    | "radiologista"
    | "endoscopista"
    | "ecografista"
    | "cardiologista"
    | "outro";
  conclusaoLiteralGeradaEm?: string;
  conclusaoLiteralStatus?: "sucesso" | "sem_secao_conclusao" | "erro_extracao";
}

interface ExameImagem {
  id: string;
  tipo: string;
  data?: string;
  dataExame?: string;
  descricao?: string;
  achados?: AchadosExame; // ✅ Agora é objeto estruturado
  resumoAchados?: string;
  url?: string;
  nomeArquivo?: string;
}

/**
 * 📊 Formata os achados estruturados de um exame anterior para comparação
 */
function formatarAchadosParaComparacao(exame: ExameImagem): string {
  const data = exame.dataExame || exame.data || "Data não informada";
  const achados = exame.achados;

  if (!achados) {
    // Se não tem achados estruturados, usa resumo ou descrição
    if (exame.resumoAchados) {
      return `📅 ${data}: ${exame.resumoAchados}`;
    }
    if (exame.descricao) {
      return `📅 ${data}: ${exame.descricao} (sem análise IA)`;
    }
    return `📅 ${data}: Sem achados registrados`;
  }

  // Formatar achados estruturados
  const partes: string[] = [];

  if (
    achados.lesoesHepaticas &&
    achados.lesoesHepaticas !== "Nenhuma identificada"
  ) {
    partes.push(`Lesões: ${achados.lesoesHepaticas}`);
  }

  if (achados.statusMilao) {
    partes.push(`Milão: ${achados.statusMilao}`);
  }

  if (achados.trombosePortal && achados.trombosePortal !== "NÃO") {
    partes.push(`TVP: ${achados.trombosePortal}`);
    if (achados.tipoTrombose) {
      partes.push(`Tipo: ${achados.tipoTrombose}`);
    }
  }

  if (achados.shunts && achados.shunts !== "Nenhum identificado") {
    partes.push(`Shunts: ${achados.shunts}`);
  }

  if (achados.ascite === "SIM") {
    partes.push("Ascite: SIM");
  }

  if (achados.esplenomegalia && achados.esplenomegalia.startsWith("SIM")) {
    partes.push(`Esplenomegalia: ${achados.esplenomegalia}`);
  }

  if (partes.length === 0) {
    return `📅 ${data}: Sem achados significativos`;
  }

  return `📅 ${data}:\n   ${partes.join("\n   ")}`;
}

/**
 * 📈 Gera tabela de comparação temporal para a IA
 */
function gerarTabelaComparacao(examesAnteriores: ExameImagem[]): string {
  const examesComAchados = examesAnteriores.filter((e) => e.achados);

  if (examesComAchados.length === 0) {
    return "Nenhum exame anterior com achados estruturados disponível para comparação.";
  }

  let tabela = `| Data | Lesões | Milão | TVP | Tipo TVP | Shunts |
|------|--------|-------|-----|----------|--------|
`;

  examesComAchados.forEach((e) => {
    const a = e.achados!;
    const data = e.dataExame || e.data || "-";
    const lesoes = a.lesoesHepaticas?.substring(0, 30) || "-";
    const milao = a.statusMilao || "-";
    const tvp = a.trombosePortal?.includes("SIM") ? a.trombosePortal : "NÃO";
    const tipoTvp = a.tipoTrombose || "-";
    const shunts = a.shunts?.substring(0, 20) || "-";

    tabela += `| ${data} | ${lesoes} | ${milao} | ${tvp} | ${tipoTvp} | ${shunts} |\n`;
  });

  return tabela;
}

export function gerarPromptExameImagem(
  paciente: Paciente,
  exameAtual: ExameImagem,
  examesAnteriores: ExameImagem[]
): string {
  // Dados básicos do paciente
  const idade = paciente.dataNascimento
    ? Math.floor(
        (new Date().getTime() - new Date(paciente.dataNascimento).getTime()) /
          (365.25 * 24 * 60 * 60 * 1000)
      )
    : "não informada";

  const sexo = paciente.sexo === "M" ? "Masculino" : "Feminino";

  // Situação especial (CHC)
  const situacaoEspecial = paciente.inscricaoTx?.situacaoEspecial;
  const tipoSE = paciente.inscricaoTx?.tipoSE;
  const meldCorrigido = paciente.inscricaoTx?.meldCorrigido;

  // Candidato a transplante?
  const candidatoTx =
    paciente.tipoAtendimento === "figado" &&
    !paciente.inscricaoTx?.dataTransplante &&
    !paciente.dataTransplante;

  // Pós-transplante?
  const dataTransplante =
    paciente.inscricaoTx?.dataTransplante || paciente.dataTransplante;
  const isPosTx = !!dataTransplante;
  let tempoPosTx = "";
  if (isPosTx && dataTransplante) {
    const dtStr = String(dataTransplante);
    let dtISO = dtStr;
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(dtStr)) {
      const p = dtStr.split("/");
      dtISO = `${p[2]}-${p[1]}-${p[0]}`;
    }
    const dias = Math.floor(
      (new Date().getTime() - new Date(dtISO + "T12:00:00").getTime()) /
        (1000 * 60 * 60 * 24)
    );
    const anos = Math.floor(dias / 365);
    const meses = Math.floor((dias % 365) / 30);
    tempoPosTx =
      anos > 0
        ? `${anos} ano(s) e ${meses} mês(es)`
        : `${meses} mês(es) e ${dias % 30} dia(s)`;
  }
  // Child-Pugh (para recomendação de anticoagulação)
  const childPugh = paciente.inscricaoTx?.childPugh;
  let classeChild = "Não informado";
  if (childPugh) {
    if (childPugh <= 6) classeChild = "A";
    else if (childPugh <= 9) classeChild = "B";
    else classeChild = "C";
  }

  // ✅ Função auxiliar para converter data para timestamp
  const getDataTimestamp = (exame: ExameImagem): number => {
    const dataStr = exame.dataExame || exame.data;
    if (!dataStr) return 0;

    // Formato YYYY-MM-DD
    if (dataStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return new Date(dataStr + "T00:00:00").getTime();
    }
    // Formato DD/MM/YYYY
    if (dataStr.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
      const [dia, mes, ano] = dataStr.split("/");
      return new Date(`${ano}-${mes}-${dia}T00:00:00`).getTime();
    }
    return 0;
  };

  // Data do exame atual
  const dataExameAtual = getDataTimestamp(exameAtual);

  // ✅ Filtrar exames ANTERIORES ao atual (por data) e do mesmo tipo
  // Ordenar do mais recente para o mais antigo
  const examesDoMesmoTipo = examesAnteriores
    .filter((e) => {
      // Mesmo tipo
      if (e.tipo !== exameAtual.tipo) return false;
      // Data anterior ao exame atual
      const dataExame = getDataTimestamp(e);
      if (dataExameAtual > 0 && dataExame > 0) {
        return dataExame < dataExameAtual; // Apenas exames COM DATA ANTERIOR
      }
      return true; // Se não tem data, inclui por segurança
    })
    .sort((a, b) => getDataTimestamp(b) - getDataTimestamp(a)) // Mais recente primeiro
    .slice(0, 5);

  // ✅ Também pegar exames POSTERIORES (para contexto)
  const examesPosteriores = examesAnteriores
    .filter((e) => {
      if (e.tipo !== exameAtual.tipo) return false;
      const dataExame = getDataTimestamp(e);
      if (dataExameAtual > 0 && dataExame > 0) {
        return dataExame > dataExameAtual; // Exames com data POSTERIOR
      }
      return false;
    })
    .sort((a, b) => getDataTimestamp(a) - getDataTimestamp(b)) // Mais antigo primeiro
    .slice(0, 3);

  // ✅ NOVO: Listar TODOS os exames do paciente (todos os tipos)
  const todosExamesOrdenados = examesAnteriores.sort(
    (a, b) => getDataTimestamp(b) - getDataTimestamp(a)
  ); // Mais recente primeiro

  // ✅ NOVO: Identificar tipos únicos de exames já realizados
  const tiposExamesRealizados = examesAnteriores
    .map((e) => e.tipo)
    .filter((tipo, index, self) => self.indexOf(tipo) === index);

  // ✅ NOVO: Identificar procedimentos invasivos/diagnósticos já realizados
  // Verifica tanto o TIPO quanto a DESCRIÇÃO do exame
  const procedimentosRealizados = examesAnteriores
    .filter((e) => {
      const tipo = (e.tipo || "").toLowerCase();
      const descricao = (e.descricao || "").toLowerCase();
      const textoCompleto = tipo + " " + descricao;

      return (
        textoCompleto.includes("biopsia") ||
        textoCompleto.includes("biópsia") ||
        textoCompleto.includes("tace") ||
        textoCompleto.includes("quimioemboliza") ||
        textoCompleto.includes("ablação") ||
        textoCompleto.includes("ablacao") ||
        textoCompleto.includes("rfa") ||
        textoCompleto.includes("radiofrequencia") ||
        textoCompleto.includes("radiofrequência") ||
        textoCompleto.includes("microondas") ||
        textoCompleto.includes("drenagem") ||
        textoCompleto.includes("paracentese") ||
        textoCompleto.includes("tips") ||
        textoCompleto.includes("cateterismo") ||
        textoCompleto.includes("pet-ct") ||
        textoCompleto.includes("pet scan")
      );
    })
    .map((e) => {
      const descricaoResumida = e.descricao
        ? ` - ${e.descricao.substring(0, 50)}`
        : "";
      return `${e.tipo}${descricaoResumida} (${
        e.dataExame || e.data || "data não informada"
      })`;
    });

  // ✅ NOVO: Detectar categoria do exame atual
  // B385: nome do arquivo como dica quando o tipo não decide ("Outros").
  // ⚠️ O objeto que chega aqui em runtime (exameExpandido, ListaExamesImagem)
  // guarda o nome do PDF em `nomeArquivo`; `arquivo.nome` é o tipo declarado e
  // vinha vazio — por isso a 1ª tentativa do B385 não roteou (biópsia de 01/11,
  // 2ª rodada, ainda no template genérico). Lê os dois.
  // B385-FU2: nome de arquivo real é arbitrário ("MARIA ... REL.pdf", caso de
  // 01/11) — a DESCRIÇÃO que o médico digita entra na dica também. O que decide
  // de verdade continua sendo o TIPO do exame; dica é rede, não fonte.
  const categoriaExame = detectarCategoriaExame(
    exameAtual.tipo || "",
    [exameAtual.nomeArquivo, (exameAtual as any).arquivo?.nome, exameAtual.descricao]
      .filter(Boolean)
      .join(" ")
  );
  // ✅ AI_46: detectar cenário clínico (âncora do enquadramento)
  const cenarioClinico = detectarCenarioClinico(paciente, exameAtual);
  const blocoIndicacaoClinica = gerarBlocoIndicacaoClinica(
    exameAtual,
    cenarioClinico
  );

  // ✅ AI_47 Commit 5A.2: validação determinística do critério de risco CHC
  // Dado será consumido pelo bloco LI-RADS condicional no Commit 5B.
  // Nesta iteração apenas calcula e loga para observação empírica.
  const resultadoCriterioCHC = validarCriterioRiscoCHC(
    paciente,
    exameAtual,
    examesAnteriores
  );
  // [B196] Não logar o conteúdo clínico do critério (LGPD) — só que foi calculado.
  console.log("📊 [AI_47 5A.2] Critério LI-RADS: calculado");

  // ✅ AI_47 Commit 5B: bloco de instruções LI-RADS ramificado conforme
  // confiança do critério de risco. Substitui a ressalva metodológica
  // longa (9ª falha epistemológica — caso-âncora Suely TC abdome 04/2025)
  // por instruções proporcionais ao resultado determinístico.
  const blocoInstrucoesCriterioCHC = (() => {
    if (
      resultadoCriterioCHC.aplicavel &&
      resultadoCriterioCHC.confianca === "alta"
    ) {
      return `
**→ APLICAR LI-RADS NORMALMENTE.** Critério estruturado de risco satisfeito com alta confiança (cirrose documentada, HBV crônico ou CHC prévio). Na seção de lesões focais, mencione em UMA LINHA qual critério valida o uso do sistema. NÃO inclua ressalva metodológica longa. NÃO liste diagnósticos diferenciais genéricos por default — o diferencial específico para padrões atípicos é tratado na regra seguinte (nódulo hipervascular atípico).
`;
    }
    if (
      resultadoCriterioCHC.aplicavel &&
      resultadoCriterioCHC.confianca === "media"
    ) {
      return `
**→ APLICAR LI-RADS NORMALMENTE.** Critério radiológico proprietário (AI_46) satisfeito por sinais de hepatopatia crônica identificados no laudo ou em exames anteriores. Na seção de lesões focais, mencione em UMA LINHA: "LI-RADS aplicável — critério radiológico: evidência de hepatopatia crônica no exame (hipertensão portal, colaterais, esplenomegalia ou morfologia cirrótica). Confirmação clínica da etiologia é recomendada mas NÃO bloqueante para a classificação." NÃO inclua ressalva metodológica longa. NÃO liste diagnósticos diferenciais genéricos por default.
`;
    }
    return `
**→ VALIDAÇÃO ESTRUTURADA NÃO CONFIRMOU CRITÉRIOS.** Importante: o pipeline determinístico rodou ANTES de você ver o laudo/imagem que está analisando agora. Ao examinar o documento fornecido, VERIFIQUE ATIVAMENTE se há sinais radiológicos de hepatopatia crônica que possam validar o critério radiológico proprietário:
- Hipertensão portal (veia porta dilatada, recanalização umbilical, varizes peripancreáticas ou esplênicas)
- Esplenomegalia
- Morfologia hepática cirrótica (contornos nodulares, atrofia do lobo direito, hipertrofia do caudado)
- Colaterais portossistêmicas

**SE você identificar QUALQUER um destes marcadores no laudo atual:** aplique LI-RADS normalmente e mencione em UMA LINHA: "LI-RADS aplicável — evidência radiológica de hepatopatia crônica identificada no próprio exame."

**SE NÃO HOUVER nenhum marcador de hepatopatia crônica no laudo atual E o paciente não tiver critério estruturado:** aplique LI-RADS com ressalva BREVE (máximo 2 linhas, sem lista extensa de DDs): "⚠️ Ressalva metodológica: LI-RADS aplicado em paciente sem critério de risco para CHC documentado nem evidência radiológica de hepatopatia crônica. A classificação deve ser interpretada com cautela; correlação clínico-histopatológica pode ser necessária." O diferencial específico para padrões atípicos é tratado na regra seguinte — NÃO antecipe aqui.
`;
  })();

  // B351 (AI_152) — DOADOR VIVO: o template é escolhido pela MODALIDADE, não pelo
  // tipo do exame. A tomografia do doador se chama igual à do receptor, então o
  // tipo não os distingue; e rotear por substring já tornou template inalcançável
  // neste arquivo antes (B305/AI_139). Um exame de imagem num prontuário de doador
  // é, por definição, um exame de doador — a pergunta é adequabilidade e
  // planejamento cirúrgico, não rastreio de doença.
  const ehDoador = ehDoadorFigado(paciente.tipoAtendimento);

  // B351-FU4 (AI_152) — achado de Leonardo na 2ª validação: a análise dizia "GRWR
  // não calculável: o peso do receptor não consta", mas ele JÁ o digitou na aba de
  // Avaliação do Doador. O prompt não recebia esses dados. Agora recebe — o que
  // também permite CONFRONTAR a volumetria do laudo com a registrada.
  const blocoDadosDoador = (() => {
    if (!ehDoador) return "";
    const a: any = (paciente as any).avaliacaoDoador || {};
    const linhas: string[] = [];
    const add = (rot: string, v: any, un = "") =>
      v !== undefined && v !== null && String(v).trim() !== ""
        ? linhas.push(`- ${rot}: ${v}${un}`)
        : undefined;
    add("Enxerto pretendido", a.loboDoado);
    add("Peso do RECEPTOR", a.pesoReceptor, " kg");
    add("Peso da doadora", a.peso, " kg");
    add("Altura da doadora", a.altura);
    const imc = calcularIMC(Number(a.peso), Number(a.altura));
    if (imc !== null) linhas.push(`- IMC da doadora: ${imc} kg/m²`);
    add("Volume hepático total registrado", a.volumeFigadoTotal ?? a.pesoFigadoTotal, " cm³");
    add("Volume do enxerto registrado", a.volumeEnxerto ?? a.pesoEnxerto, " cm³");
    add("Esteatose registrada", a.esteatosePercentual, "%");
    add("Método da esteatose", a.esteatoseMetodo);
    if (!linhas.length) return "";
    return `
## 📋 DADOS JÁ REGISTRADOS NA AVALIAÇÃO DO DOADOR
${linhas.join("\n")}

**Use estes dados.** Com o peso do receptor, CALCULE o GRWR do enxerto que o laudo
permitir estimar — não escreva que ele é incalculável sem antes olhar aqui. Se a
volumetria do LAUDO divergir da registrada acima, **aponte a divergência**: uma das
duas está desatualizada, e isso importa mais que o número em si.
`;
  })();
  const templateEspecifico = ehDoador
    ? getTemplateDoadorFigado()
    : getTemplateEspecifico(categoriaExame, cenarioClinico);
  const isExameImagem = categoriaExame === "imagem_hepatica";

  // ✅ NOVO: Resumo de todos os exames por tipo
  const resumoExamesPorTipo = tiposExamesRealizados
    .map((tipo) => {
      const examesDoTipo = examesAnteriores.filter((e) => e.tipo === tipo);
      const maisRecente = examesDoTipo.sort(
        (a, b) => getDataTimestamp(b) - getDataTimestamp(a)
      )[0];
      return `- **${tipo}** (${examesDoTipo.length}x) - último: ${
        maisRecente?.dataExame || maisRecente?.data || "?"
      }`;
    })
    .join("\n");

  // ✅ Formatar exames anteriores para texto
  const examesAnterioresFormatados = examesDoMesmoTipo
    .map(formatarAchadosParaComparacao)
    .join("\n\n");

  // ✅ Formatar exames posteriores (se houver)
  const examesPosterioresFormatados = examesPosteriores
    .map(formatarAchadosParaComparacao)
    .join("\n\n");

  // ✅ Gerar tabela de comparação se houver exames com achados
  const tabelaComparacao = gerarTabelaComparacao(examesDoMesmoTipo);

  // ✅ Detectar mudanças significativas (comparar com exame anterior mais recente)
  const exameAnteriorMaisRecente = examesDoMesmoTipo.find((e) => e.achados);
  let alertasMudanca = "";

  if (exameAnteriorMaisRecente?.achados) {
    const ua = exameAnteriorMaisRecente.achados;
    alertasMudanca = `
### ⚠️ PONTOS DE ATENÇÃO PARA COMPARAÇÃO:
Ao analisar o exame atual, COMPARE especificamente:
${
  ua.lesoesHepaticas
    ? `- **Lesões hepáticas anteriores:** ${ua.lesoesHepaticas} → Houve crescimento? Novas lesões?`
    : ""
}
${
  ua.statusMilao
    ? `- **Status Milão anterior:** ${ua.statusMilao} → Mudou? Se era DENTRO, continua?`
    : ""
}
${
  ua.trombosePortal?.includes("SIM")
    ? `- **Trombose anterior:** ${ua.trombosePortal} → Progrediu? Regrediu com anticoagulação?`
    : ""
}
${
  ua.shunts
    ? `- **Shunts anteriores:** ${ua.shunts} → Novos shunts? Mudança de calibre?`
    : ""
}
${
  ua.ascite === "SIM"
    ? "- **Ascite anterior:** Presente → Aumentou? Diminuiu?"
    : ""
}
`;
  }

  // Se for exame específico (não imagem hepática), usar template próprio
  if (templateEspecifico) {
    return `
# 🏥 ANÁLISE ESPECIALIZADA - ${exameAtual.tipo?.toUpperCase() || "EXAME"}
${blocoIndicacaoClinica}
${templateEspecifico}
${blocoDadosDoador}

---

## 📋 VISÃO GERAL DE TODOS OS EXAMES DO PACIENTE:
**Tipos de exames já realizados:**
${resumoExamesPorTipo || "Nenhum exame anterior registrado."}

**⚠️ REGRA CRÍTICA:** NÃO sugira realizar exames que já constam na lista acima!

${
  procedimentosRealizados.length > 0
    ? `
### ⚠️ PROCEDIMENTOS INVASIVOS JÁ REALIZADOS:
${procedimentosRealizados.map((p) => `- ✅ ${p}`).join("\n")}
`
    : ""
}

---

## 👤 DADOS DO PACIENTE:
- **Idade atual:** ${idade} anos (calculada hoje; exames de datas anteriores terão idade menor — isso NÃO é discrepância)
- **Sexo:** ${sexo}
- **Tipo de atendimento:** ${
      isPosTx
        ? `PÓS-TRANSPLANTE HEPÁTICO (Tx há ${tempoPosTx})`
        : candidatoTx
        ? "Pré-transplante hepático"
        : "Acompanhamento hepatológico"
    }
${
  isPosTx
    ? `- **⚠️ PACIENTE TRANSPLANTADO** — Data do Tx: ${dataTransplante} (há ${tempoPosTx})`
    : ""
}
${
  situacaoEspecial
    ? isPosTx
      ? `- **Situação Especial (histórica):** ${
          tipoSE || "Tipo não especificado"
        } — resolvida pelo transplante`
      : `- **Situação Especial (ativa):** SIM - ${
          tipoSE || "Tipo não especificado"
        }`
    : ""
}
${meldCorrigido ? `- **MELD Corrigido:** ${meldCorrigido}` : ""}

---

## 🔬 EXAME ATUAL A SER ANALISADO:
- **Tipo:** ${exameAtual.tipo}
- **Data:** ${exameAtual.dataExame || exameAtual.data || "Não informada"}
${exameAtual.descricao ? `- **Descrição:** ${exameAtual.descricao}` : ""}

**[O LAUDO/DOCUMENTO SERÁ FORNECIDO JUNTO COM ESTE PROMPT]**

---
${REGRA_DIVERGENCIA_CAMPO_DADOS}
${REGRA_CONDUTA_SOBRE_DADO_AUSENTE}
🔬 ${REGRA_TUMORES_MULTIPLOS_IMAGEM}
🩻 ${REGRA_LIRADS_IMAGEM}
🧭 ${REGRA_ESCOPO_SETORIAL}
${REGRA_SEM_META_FOOTER}

---

Analise o documento fornecido e responda conforme a estrutura específica acima.
`;
  }

  // Template padrão para exames de imagem hepática (TC, RM, US, Doppler)
  return `
# 🏥 ANÁLISE ESPECIALIZADA DE EXAME DE IMAGEM - HEPATOLOGIA E TRANSPLANTE

## ⚠️ REGRAS FUNDAMENTAIS - LEIA COM ATENÇÃO:
### 🚫 ESCOPO RESTRITO - NÃO ULTRAPASSAR:
Esta análise é EXCLUSIVAMENTE sobre o exame fornecido.
- ❌ NÃO mencionar exames laboratoriais (AFP, bilirrubina, MELD, etc.)
- ❌ NÃO sugerir coleta de exames de sangue
- ❌ NÃO comentar sobre marcadores tumorais
- ❌ NÃO sugerir exames que JÁ FORAM REALIZADOS (veja seção "VISÃO GERAL DE TODOS OS EXAMES")
- ✅ FOCAR apenas nos achados do exame atual
- ✅ A integração com outros exames será feita na "Análise Geral da IA"
- ❌ NÃO incluir eixos de candidatura a transplante (fragilidade/sarcopenia, rastreio infeccioso/sorológico, vacinação, avaliação psicossocial/adesão) nem manejo clínico geral — isso é EXCLUSIVO da Análise Geral, NUNCA desta análise de exame isolado (AI_118).

### 🔒 NÃO TRANSCREVER O CABEÇALHO DO DOCUMENTO (B365, AI_156 — OBRIGATÓRIO):
O PDF do laudo traz o cabeçalho de identificação do serviço. Analise os ACHADOS, não a capa do documento.
- ❌ NUNCA copie para a resposta o NOME do paciente, número de prontuário/matrícula, CPF, RG, CNS, data de nascimento, telefone ou endereço — mesmo que estejam no documento e mesmo ao descrever que o laudo está incompleto.
- ✅ Para dizer DE QUEM/DE QUANDO é o exame, use apenas: tipo de exame, DATA do exame, serviço/clínica e o nome do médico laudista com CRM (são dados do documento, não do paciente).
- ✅ Idade e sexo podem ser citados (não identificam).
- Ao relatar laudo truncado ou sem corpo descritivo, diga O QUE FALTA (técnica, parênquima, vias biliares, vasos, conclusão) — não liste o conteúdo do cabeçalho como prova.

### 🔁 SEGUIMENTO — QUANDO REPETIR ESTE EXAME (OBRIGATÓRIO):
A análise DEVE indicar, nas recomendações, QUANDO repetir/seguir ESTE exame, com INTERVALO CONCRETO ancorado no achado e na diretriz aplicável — NUNCA escrever apenas "conforme protocolo" sem o intervalo. Exemplos: EDA pós-ligadura de varizes → repetir em 1–4 semanas até erradicação, depois vigilância endoscópica (ex.: a cada 3–6 meses após erradicação, conforme Baveno VII/AASLD); EDA de rastreio sem varizes em cirrótico compensado → repetir em ~2 anos; nódulo LR-3 → reavaliar em 3–6 meses. Use os intervalos das diretrizes recuperadas quando disponíveis; se não houver base para um número exato, dê a FAIXA recomendada — nunca omita o "quando".

### 🔬 REGRA ESPECIAL PARA BIÓPSIA/ANATOMOPATOLÓGICO:
Se o exame atual for uma BIÓPSIA ou laudo ANATOMOPATOLÓGICO:
- ✅ FOQUE EXCLUSIVAMENTE nos achados histopatológicos
- ❌ NÃO discuta achados de imagem (tamanho de lesões, critérios de Milão, LI-RADS)
- ❌ NÃO sugira realizar TC, RM ou outros exames de imagem - eles JÁ EXISTEM no histórico
- ❌ NÃO mencione "estadiamento por imagem" se já existem TC/RM no histórico
- ✅ Mencione: "Os exames de imagem já realizados devem ser correlacionados na Análise Geral"
- ✅ Foque em: grau histológico, padrão arquitetural, diferenciação, margens, invasão vascular microscópica

### 🚫 REGRA CRÍTICA - EXAMES JÁ REALIZADOS:
**NUNCA sugira realizar um exame que já consta no histórico do paciente!**
- Antes de recomendar qualquer exame, VERIFIQUE a seção "VISÃO GERAL DE TODOS OS EXAMES"
- Se o exame já existe → NÃO sugira realizá-lo
- Em vez disso, diga: "Correlacionar com [exame] já realizado em [data]"

### ⚖️ REGRA DE EQUILÍBRIO - MUITO IMPORTANTE:
**Todos os achados críticos devem receber IGUAL DESTAQUE na análise!**
- Se há CHC E trombose portal, ambos devem ter seções de tamanho similar
- Não dedique 2 páginas para trombose e 1 parágrafo para CHC (ou vice-versa)
- Use formatação visual similar (tabelas, boxes) para todos os achados críticos
- CHC LI-RADS 5 é tão importante quanto trombose portal complexa

### 🚫 REGRA DE FORMATAÇÃO - BOXES COLORIDOS:
**Na seção de RECOMENDAÇÕES (seção 8), NÃO use boxes coloridos/destacados!**
- Boxes são criados quando você usa o símbolo maior-que no início da linha (blockquote)
- NA SEÇÃO 8, não use blockquotes para criar boxes
- Use apenas títulos com 4 hashtags e listas numeradas para itens
- Isso garante que CHC e Trombose tenham mesma formatação

${
  isPosTx
    ? `
### 🏥 REGRAS OBRIGATÓRIAS PARA PACIENTE PÓS-TRANSPLANTE:
**Este paciente é TRANSPLANTADO HEPÁTICO (Tx há ${tempoPosTx}).** Todas as interpretações DEVEM considerar este contexto:

1. **VESÍCULA BILIAR:** A colecistectomia é SEMPRE realizada durante o transplante hepático. Se o laudo mencionar "vesícula não visualizada" ou "ausência de vesícula", NÃO questione — é consequência do transplante, não achado novo.

2. **CHC/HCC PRÉVIO:** Se o paciente tinha CHC como indicação do transplante, o tumor foi REMOVIDO junto com o fígado nativo. NÃO alerte sobre "exame inadequado para estadiamento de CHC" — o CHC já foi tratado. A vigilância pós-Tx é para RECIDIVA no enxerto (rara, ~10-15%), não para o tumor original. A Situação Especial por CHC é HISTÓRICA — foi o critério de priorização na lista de espera, já cumprido pelo transplante. NÃO se refira a ela como "ativa" ou "Situação Especial do paciente".

3. **COMPLICAÇÕES BILIARES PÓS-TX (foco principal):**
   - Estenose anastomótica (mais comum, tratável por CPRE/dilatação)
   - Estenose não-anastomótica/isquêmica (mais grave, sugere lesão de preservação ou TAH)
   - Fístula biliar
   - Bilomas
   - Colangioressonância e CPRE são os exames de escolha para avaliação biliar pós-Tx

4. **COMPLICAÇÕES VASCULARES PÓS-TX:**
   - Trombose de artéria hepática (TAH) — emergência, pode causar necrose biliar
   - Estenose de artéria hepática
   - Trombose/estenose portal
   - Estenose de veias hepáticas / síndrome de Budd-Chiari do enxerto

   5. **REJEIÇÃO E IMUNOSSUPRESSÃO — ESCOPO DESCRITIVO (esta análise NÃO prescreve tratamento):**
   - Sinais de rejeição por imagem são INESPECÍFICOS — diagnóstico definitivo é por biópsia do enxerto. NÃO diagnosticar rejeição por imagem.
   - Imunossupressão de manutenção, ajustes, níveis séricos-alvo, conversões de classe (inibidor de calcineurina ↔ inibidor de mTOR), desmame de corticoide e manejo terapêutico de complicações relacionadas (PTLD, nefrotoxicidade, infecções oportunistas, malignidade de novo, recidiva no enxerto) são competência do time assistente e da Análise Geral — NÃO desta análise de exame isolado.
   - PERMITIDO: menção conceitual de alto nível quando diretamente relevante à interpretação do achado (ex.: "PTLD tem manejo específico com redução de imunossupressão ± rituximabe em EBV+", "nefrotoxicidade por inibidor de calcineurina justifica avaliação de conversão").
   - PROIBIDO: nomear drogas específicas da manutenção (tacrolimus, ciclosporina, sirolimus, everolimus, micofenolato, azatioprina, prednisona), especificar doses, níveis séricos-alvo, esquemas de conversão/desmame, protocolos terapêuticos detalhados, prazos de biópsia de controle.
   - Em achado sugestivo de rejeição, PTLD, recidiva no enxerto, TAH ou complicação biliar isquêmica, a ação concreta é **referir ao time assistente / discussão multidisciplinar urgente**, não prescrever.

6. **RECIDIVA DA DOENÇA DE BASE:** Hepatite C, hepatite autoimune, CEP podem recidivar no enxerto.

7. **INDICAÇÃO DO EXAME:** Em paciente pós-Tx, o exame de imagem foi provavelmente solicitado para investigar uma COMPLICAÇÃO ESPECÍFICA do transplante (biliar, vascular, recidiva), NÃO para rastreamento da doença original.
`
    : ""
}

### 🛑 REGRA CRÍTICA: NÃO CONCLUIR SEM DADOS SUFICIENTES
**NUNCA faça conclusões definitivas quando faltam informações no laudo.**
- Se um dado necessário para classificação não está descrito → DIGA QUE FALTA
- Use frases como: "Não é possível determinar com certeza pois o laudo não descreve..."
- Indique sempre O QUE ESTÁ FALTANDO para completar a avaliação
- Prefira intervalos (ex: "Yerdel 3 a 4") quando não há dados para precisar

### 📚 CONCEITOS TÉCNICOS OBRIGATÓRIOS:

#### FASES DE AQUISIÇÃO DA TC/RM (NÃO CONFUNDIR COM VIA DE INJEÇÃO):
O contraste é SEMPRE administrado por via VENOSA PERIFÉRICA.
As "fases" referem-se ao TEMPO de aquisição das imagens após a injeção:

| Fase | Tempo após injeção | O que avalia |
|------|-------------------|--------------|
| **Fase arterial** | 25-30 segundos | Realce arterial hepático, hipervascularização |
| **Fase portal/venosa** | 60-70 segundos | Realce do parênquima, veia porta |
| **Fase tardia/equilíbrio** | 3-5 minutos | Washout, retenção de contraste |

**IMPORTANTE:** 
- "Administração venosa do contraste" = via de injeção (SEMPRE venosa em TC)
- Isso NÃO significa "apenas fase venosa"
- **NÃO CRITIQUE A TÉCNICA do exame a menos que o laudo EXPLICITAMENTE indique limitação**

#### CRITÉRIOS LI-RADS (Liver Imaging Reporting and Data System):
| Categoria | Significado | Conduta |
|-----------|-------------|---------|
| LR-1 | Definitivamente benigno | Sem seguimento específico |
| LR-2 | Provavelmente benigno | Seguimento de rotina |
| LR-3 | Probabilidade intermediária | Seguimento em 3-6 meses |
| LR-4 | Provavelmente CHC | Discutir tratamento ou biópsia |
| LR-5 | Definitivamente CHC | Tratamento sem biópsia |
| LR-M | Malignidade não-CHC | Biópsia recomendada |
| LR-TIV | Invasão tumoral em veia | DROP-OUT da lista |

#### ⚠️ CRITÉRIOS DE MILÃO - ATENÇÃO À CONVERSÃO DE UNIDADES:

**IMPORTANTE: Os laudos geralmente descrevem medidas em MILÍMETROS (mm).**
**Você DEVE converter para centímetros antes de avaliar os critérios de Milão!**

**Conversão: dividir mm por 10 para obter cm**
- 37 mm = 3,7 cm ✅ DENTRO de Milão
- 50 mm = 5,0 cm ✅ DENTRO de Milão (limite)
- 51 mm = 5,1 cm ❌ FORA de Milão
- 30 mm = 3,0 cm ✅ DENTRO de Milão

**Critérios de Milão para elegibilidade ao transplante:**
- Nódulo ÚNICO ≤ 5 cm (≤ 50 mm), OU
- Até 3 nódulos, cada um ≤ 3 cm (≤ 30 mm)
- SEM invasão vascular macroscópica
- SEM metástases extra-hepáticas

**Exemplo de análise correta:**
- Laudo: "nódulo de 37 mm" → 37 ÷ 10 = 3,7 cm → DENTRO de Milão ✅
- Laudo: "nódulo de 52 mm" → 52 ÷ 10 = 5,2 cm → FORA de Milão ❌

---

## 🩸 PROTOCOLO PARA TROMBOSE DE VEIA PORTA (SE PRESENTE NO LAUDO):

**IMPORTANTE:** Se o laudo mencionar QUALQUER um destes achados, APLICAR OBRIGATORIAMENTE este protocolo:
- Trombose de veia porta / trombo portal
- Trombose da veia esplênica
- Trombose da veia mesentérica superior (VMS)
- Transformação cavernomatosa / cavernoma portal
- Oclusão portal

### CLASSIFICAÇÃO DE YERDEL - ATENÇÃO AOS DADOS DISPONÍVEIS:

| Grau | Extensão da Trombose | Estratégia Cirúrgica no Transplante |
|------|----------------------|-------------------------------------|
| **1** | < 50% da luz da veia porta | Trombectomia simples |
| **2** | > 50% da luz OU oclusão completa da VP | Tromboendovenectomia |
| **3** | VP + VMS distal (proximal à confluência esplenomesentérica) | Jump graft VMS → VP do enxerto |
| **4** | VP + VMS proximal + Veia Esplênica (trombose esplâncnica difusa) | Anastomose reno-portal, cavo-portal ou alternativas |

**⚠️ REGRA CRÍTICA PARA CLASSIFICAÇÃO DE YERDEL:**

Para classificar como Yerdel 4, o laudo DEVE EXPLICITAMENTE mencionar:
- Trombose da VMS PROXIMAL (porção distal ao ligamento de Treitz)

Se o laudo menciona apenas:
- Trombose da veia esplênica + junção espleno-mesentérica + tronco portal
- MAS NÃO menciona a VMS distal/proximal

**Você DEVE classificar como:**
> "**Yerdel no mínimo 3** (VP + veia esplênica + confluência). Não é possível determinar se é Yerdel 4 pois o laudo não descreve a perviedade da porção distal da veia mesentérica superior. **Recomenda-se AngioTC ou AngioRM com reconstrução vascular para avaliar extensão completa da trombose na VMS.**"

**NÃO assuma Yerdel 4 sem evidência explícita de trombose da VMS proximal!**

### DIFERENCIAÇÃO: TROMBOSE TUMORAL vs BLAND (Critérios A-VENA)

**CRÍTICO em pacientes com CHC:** Trombose TUMORAL = DROP-OUT da lista de transplante!

| Critério | Trombose TUMORAL | Trombose BLAND (não tumoral) |
|----------|------------------|------------------------------|
| Realce arterial do trombo | PRESENTE (hipervascular) | AUSENTE |
| Expansão da veia porta | > 23 mm (abaulamento) | Normal ou leve dilatação |
| Neovasculatura no trombo | PRESENTE | AUSENTE |
| Continuidade com lesão hepática | PRESENTE | AUSENTE |

**Características que sugerem trombo BLAND/plaquetário:**
- Laudo menciona "características de trombo plaquetário"
- Ausência de realce arterial do trombo
- Calibre da VP ≤ 23 mm (mesmo se aumentado)
- Sem continuidade com lesão hepática

### AVALIAÇÃO DE COLATERAIS/SHUNTS (crucial para planejamento cirúrgico):
Documentar OBRIGATORIAMENTE a presença de:
- ✅ **Shunt esplenorrenal** → Pode ser usado para anastomose RENO-PORTAL (1ª opção em Yerdel 4)
- ✅ **Varizes pericoledocianas** → Podem ser usadas para anastomose variz → VP
- ✅ **Veia gástrica esquerda calibrosa** → Anastomose gástrica → VP
- ✅ **Recanalização paraumbilical** → Documenta hipertensão portal
- ✅ **Outras colaterais portossistêmicas**

### TEMPO DE EVOLUÇÃO DA TROMBOSE:
- **AGUDA (< 6 meses):** Trombo hiperdenso/isodenso, VP dilatada, sem cavernoma, sem colaterais extensas
- **CRÔNICA (> 6 meses):** Transformação cavernomatosa, colaterais estabelecidas, síndrome pós-trombótica

### 💊 RECOMENDAÇÕES DE ANTICOAGULAÇÃO (baseadas no Child-Pugh do paciente):

**Dados do paciente:** ${
    candidatoTx ? "Candidato a transplante" : "Não candidato a Tx"
  } | Child-Pugh: ${classeChild}

| Child-Pugh | 1ª Escolha | Alternativas | EVITAR |
|------------|------------|--------------|--------|
| **A** | HBPM, Varfarina ou DOACs | Todas as opções são seguras | - |
| **B** | HBPM (Enoxaparina) | Varfarina (INR 2-3), Apixabana 5mg 12/12h com cautela | Rivaroxabana, Edoxabana |
| **C** | HBPM (Enoxaparina) | Nenhuma alternativa segura | TODOS os DOACs (contraindicados) |

**Doses padrão:**
- HBPM (Enoxaparina): 1 mg/kg 12/12h (dose plena) ou 1,5 mg/kg 1x/dia
- Varfarina: INR alvo 2,0-3,0 (monitorar de perto em cirróticos)
- Apixabana: 5 mg 12/12h (APENAS Child A; com cautela em Child B)
- Rivaroxabana: 15-20 mg/dia (APENAS Child A; EVITAR em Child B/C)
- Edoxabana: 60 mg/dia (APENAS Child A; EVITAR em Child B/C)

**REGRA PARA RECOMENDAR ANTICOAGULAÇÃO:**
- Se Child-Pugh A: pode sugerir DOACs (Apixabana ou Rivaroxabana) como opção
- Se Child-Pugh B: preferir HBPM; Apixabana é alternativa com cautela; EVITAR Rivaroxabana
- Se Child-Pugh C: APENAS HBPM; DOACs são CONTRAINDICADOS
- Se Child-Pugh não informado: recomendar protocolo conservador com HBPM

**Duração:** Mínimo 6 meses ou até transplante. Indefinido se trombofilia documentada ou extensão mesentérica.

### 🔧 OPÇÕES CIRÚRGICAS NO TRANSPLANTE:

**Para Yerdel 1-2:** Trombectomia/tromboendovenectomia - anastomose porto-portal padrão
**Para Yerdel 3:** Jump graft da VMS para VP do enxerto
**Para Yerdel 4 (ordem de preferência):**
1. **Anastomose Reno-Portal** (se shunt esplenorrenal presente) - FISIOLÓGICA
2. **Anastomose Cavo-Portal** (hemitransposição) - NÃO-FISIOLÓGICA
3. **Arterialização da Veia Porta** - NÃO-FISIOLÓGICA, último recurso

---

## 📋 DADOS DO PACIENTE:

- **Sexo:** ${sexo}
- **Idade atual:** ${idade} anos (calculada hoje; exames de datas anteriores terão idade menor — isso NÃO é discrepância)
- **Tipo Sanguíneo:** ${paciente.tipoSanguineo || "Não informado"}
- **Diagnóstico de Base:** ${
    paciente.inscricaoTx?.diagnosticoBase || "Não informado"
  }

  ### 🎯 CONTEXTO DE RISCO PARA CHC — VALIDAÇÃO DETERMINÍSTICA (AI_47)

  **Este bloco é EXCLUSIVAMENTE para validar a aplicabilidade dos sistemas de classificação radiológica** (LI-RADS, mRECIST, critérios de Milão, BCLC). Ele NÃO autoriza recomendações terapêuticas nem comentários sobre marcadores tumorais — a regra de escopo restrito permanece válida para tudo o mais.
  
  **Resultado pré-computado pelo pipeline determinístico:**
  - Diagnóstico de base: **${
    paciente.inscricaoTx?.diagnosticoBase || "Não informado"
  }**
  - LI-RADS aplicável: **${
    resultadoCriterioCHC.aplicavel
      ? "✅ SIM"
      : "⚠️ NÃO VALIDADO POR DADOS ESTRUTURADOS"
  }**
  - Critério acionado: **${resultadoCriterioCHC.criterio || "nenhum"}**
  - Confiança: **${resultadoCriterioCHC.confianca}**
  - Justificativa: ${resultadoCriterioCHC.justificativa}
  
  **Instruções específicas conforme o resultado acima:**
  ${blocoInstrucoesCriterioCHC}
  
  **REGRA DE OURO:** A ressalva metodológica, quando aplicável, é EXCEÇÃO à regra de escopo restrito e existe APENAS para validar a aplicabilidade do sistema de classificação. NÃO recomende AFP, CA 19-9, sorologias, biópsia ou outros exames nessa ressalva — isso permanece competência da Análise Geral.

### 🩺 REGRA — DIAGNÓSTICO DIFERENCIAL OBRIGATÓRIO PARA NÓDULO HIPERVASCULAR ATÍPICO

Em paciente com fator de risco documentado (cirrose, HBV, HCV crônico ou CHC prévio), quando um nódulo apresentar SIMULTANEAMENTE:

(a) **Hipervascularização arterial** AND
(b) **Ausência de washout típico na fase portal/tardia** AND
(c) **Tamanho ≥ 1 cm**

Você DEVE incluir, na seção de lesões focais e nos achados críticos, menção EXPLÍCITA ao diferencial com:
- Colangiocarcinoma intra-hepático (iCCA) — especialmente subtipo hipervascular pequeno
- HCC-CCA combinado
- Outras neoplasias não-CHC (metástase hipervascular, neuroendócrino)

**A presença simultânea de linfonodomegalia hilar ou portocava > 1 cm REFORÇA** a hipótese de iCCA (CHC isolado raramente metastatiza para linfonodos sem invasão vascular prévia), mas linfonodos podem ser reativos em até ~30% dos casos — portanto a regra é **abrir o diferencial**, NÃO afirmar diagnóstico.

A categoria LI-RADS apropriada para "hipervascular sem washout" é frequentemente **LR-M** (probably or definitely malignant, not specific for HCC), não LR-3. Considere LR-M sempre que o padrão for atípico para CHC clássico.

A ressalva e o diferencial devem ser mencionados na seção de lesões focais. A conduta concreta (CA 19-9, biópsia, encurtamento de intervalo) NÃO é responsabilidade desta análise — apenas mencione que "a investigação dirigida deve ser considerada na Análise Geral".

### 🧭 REGRA — COMPATIBILIDADE ANATÔMICA EM COMPARAÇÃO SEQUENCIAL

Ao comparar lesões entre exames sequenciais (tabela evolutiva), uma lesão SÓ pode ser tratada como "a mesma lesão evoluindo" se a localização anatômica descrita for compatível: mesmo segmento de Couinaud, OU segmentos imediatamente adjacentes COM descrição explícita de continuidade pelo radiologista.

Quando as localizações divergem entre exames (exemplo: nódulo descrito em "segmento V" em um exame e "junção VI/VII" em outro), você DEVE explicitar a ambiguidade na tabela comparativa com a anotação:

> "⚠️ Localização anatômica divergente entre exames — possível: (a) mesma lesão com descrição variável entre radiologistas, (b) lesões distintas, (c) erro de localização. Tratar como lesões potencialmente independentes até confirmação."

NÃO assuma identidade de lesão por proximidade temporal e tamanho similar — a ancoragem anatômica é obrigatória.

### 📌 REGRA — PRESERVAÇÃO DE TODOS OS ACHADOS LESIONAIS

Toda menção a "focos", "nódulos", "lesões", "imagens nodulares" ou "áreas" no laudo radiológico — mesmo qualificadas como "inespecíficos", "esparsos", "subcentimétricos", "incidentais", "indeterminados" ou "sem importância clínica aparente" — DEVE ser preservada na seção de lesões focais ou em "Outros achados". A decisão sobre relevância clínica é da camada de raciocínio diagnóstico, não da camada de extração descritiva.

Em paciente com fator de risco para CHC ou histórico de neoplasia hepática, "outros focos hipervasculares subcentimétricos inespecíficos" são potencialmente significativos e devem ser registrados explicitamente como achado a vigiar.
- **Candidato a Transplante:** ${candidatoTx ? "SIM" : "NÃO"}
${
  childPugh
    ? `- **Child-Pugh:** Classe ${classeChild} (${childPugh} pontos)`
    : ""
}
${
  situacaoEspecial
    ? isPosTx
      ? `- **Situação Especial (histórica):** ${
          tipoSE || "Tipo não especificado"
        } — resolvida pelo transplante`
      : `- **Situação Especial (ativa):** SIM - ${
          tipoSE || "Tipo não especificado"
        }`
    : ""
}
${meldCorrigido ? `- **MELD Corrigido:** ${meldCorrigido}` : ""}

---

## 🔬 EXAME ATUAL A SER ANALISADO:

- **Tipo:** ${exameAtual.tipo}
- **Data:** ${exameAtual.dataExame || exameAtual.data || "Não informada"}
${exameAtual.descricao ? `- **Descrição:** ${exameAtual.descricao}` : ""}

**[O LAUDO/IMAGEM SERÁ FORNECIDO JUNTO COM ESTE PROMPT]**

---

## 📋 VISÃO GERAL DE TODOS OS EXAMES DO PACIENTE:
**Tipos de exames já realizados:**
${resumoExamesPorTipo || "Nenhum exame anterior registrado."}

**⚠️ REGRA CRÍTICA:** NÃO sugira realizar exames que já constam na lista acima!
- Se já existe Tomografia → NÃO sugira "realizar TC"
- Se já existe Ressonância → NÃO sugira "realizar RM"  
- Se já existe Biópsia → NÃO sugira "realizar biópsia"
- Se já existe Elastografia → NÃO sugira "realizar elastografia"
Em vez disso, referencie os exames já realizados: "conforme TC de [data]..." ou "a RM já realizada demonstrou..."

${
  procedimentosRealizados.length > 0
    ? `
### ⚠️ PROCEDIMENTOS INVASIVOS JÁ REALIZADOS:
${procedimentosRealizados.map((p) => `- ✅ ${p}`).join("\n")}
`
    : ""
}

---

## 📊 HISTÓRICO DE EXAMES PARA COMPARAÇÃO TEMPORAL:

${
  examesPosteriores.length > 0
    ? `
### ⚠️ ATENÇÃO: EXISTEM EXAMES MAIS RECENTES!
**O exame que você está analisando NÃO é o mais recente do paciente.**
**Data deste exame:** ${exameAtual.dataExame || exameAtual.data}

### 📅 EXAMES POSTERIORES A ESTE (mais recentes):
${examesPosterioresFormatados}

**IMPORTANTE:** Compare este exame com os POSTERIORES para ver a EVOLUÇÃO REAL:
- Se a lesão era maior neste exame e menor nos posteriores → REGRESSÃO/RESPOSTA
- Se a lesão era menor neste exame e maior nos posteriores → PROGRESSÃO
`
    : ""
}

${
  examesDoMesmoTipo.length > 0
    ? `
### 📅 EXAMES ANTERIORES A ESTE:
**Quantidade:** ${examesDoMesmoTipo.length} exame(s) do mesmo tipo

${
  examesDoMesmoTipo.some((e) => e.achados)
    ? `
### 📈 TABELA COMPARATIVA DE ACHADOS ANTERIORES:
${tabelaComparacao}
`
    : ""
}

### 📋 DETALHES DOS EXAMES ANTERIORES:
${examesAnterioresFormatados}

${alertasMudanca}
`
    : "Nenhum exame anterior do mesmo tipo disponível para comparação."
}

**REGRA DE COMPARAÇÃO TEMPORAL:**
1. Compare SEMPRE com exames de data ANTERIOR (para ver se houve progressão ATÉ este exame)
2. Se existem exames POSTERIORES, mencione que a evolução real é vista nos exames mais recentes
3. Não confunda a ordem cronológica!

---

## 📝 ESTRUTURA DA RESPOSTA ESPERADA:

### 1. RESUMO DO EXAME
- Tipo de exame realizado
- Data
- Protocolo utilizado (se mencionado no laudo)

### 2. AVALIAÇÃO HEPÁTICA
- Dimensões e morfologia do fígado
- Ecogenicidade/atenuação do parênquima
- Sinais de hepatopatia crônica/cirrose
- Perviedade vascular (veias hepáticas, veia porta)

### 3. LESÕES FOCAIS HEPÁTICAS - ⚠️ SEÇÃO CRÍTICA

**🔴 IMPORTANTE: Se houver CHC (LI-RADS 4 ou 5), esta seção deve ter IGUAL DESTAQUE que a seção de trombose portal!**

Para CADA lesão identificada, criar um BLOCO DE DESTAQUE com:

| Item | Informação |
|------|------------|
| **Localização** | Segmento hepático (Couinaud) |
| **Dimensões** | X mm = Y cm (SEMPRE converter!) |
| **Características** | Realce arterial, washout, cápsula, etc. |
| **Classificação LI-RADS** | LR-1 a LR-5, LR-M ou LR-TIV |
| **Status Milão** | ✅ DENTRO ou ❌ FORA (mostrar cálculo) |
| **Conduta sugerida** | Vigilância / TACE / Ablação / Tx |

**Exemplo de formatação para CHC:**

> ### 🔴 HEPATOCARCINOMA CELULAR IDENTIFICADO (LI-RADS 5):
> 
> | Item | Achado |
> |------|--------|
> | **Localização** | Segmento VI |
> | **Dimensões** | 37 mm = **3,7 cm** |
> | **Características** | Hipervascular com washout e pseudocápsula |
> | **LI-RADS** | **LR-5** (definitivamente CHC) |
> | **Status Milão** | ✅ **DENTRO** (3,7 cm < 5,0 cm para nódulo único) |
> | **Elegibilidade Tx** | ELEGÍVEL pelos critérios de imagem |
>
> **Conduta para CHC:**
> - Dispensa biópsia (LI-RADS 5)
> - Considerar terapia ponte (TACE/ablação) enquanto aguarda Tx
> - TC/RM a cada 3 meses para monitorização

### 4. SISTEMA VASCULAR - ⚠️ SEÇÃO CRÍTICA

- Veia porta: perviedade, calibre, fluxo
- Veias hepáticas
- Artéria hepática
- Colaterais portossistêmicas

**🩸 SE HOUVER TROMBOSE DE VEIA PORTA:**

| Item | Resposta | Dado disponível no laudo? |
|------|----------|---------------------------|
| **Classificação de Yerdel** | (especificar ou dar intervalo) | SIM/NÃO/PARCIAL |
| **Tipo de trombose** | TUMORAL ou BLAND | SIM/NÃO |
| **Extensão para VMS** | SIM/NÃO/NÃO DESCRITO | - |
| **Extensão para V. Esplênica** | SIM/NÃO | - |
| **Shunt esplenorrenal presente** | SIM/NÃO | - |
| **Outras colaterais utilizáveis** | Descrever | - |
| **Tempo de evolução** | AGUDA ou CRÔNICA | - |

**Se faltar informação para classificar Yerdel precisamente:**
> "⚠️ **Dados insuficientes para classificação precisa.** O laudo não descreve [especificar o que falta]. Classificação possível: Yerdel X a Y. Recomenda-se [exame complementar] para definição."

### 5. OUTROS ACHADOS RELEVANTES
- Vias biliares
- Vesícula biliar
- Baço (tamanho - esplenomegalia?)
- Ascite
- Outros órgãos abdominais

### 6. COMPARAÇÃO COM EXAMES ANTERIORES

**⚠️ SEÇÃO OBRIGATÓRIA SE HOUVER EXAMES ANTERIORES COM ACHADOS!**

Se houver exames anteriores listados acima, você DEVE analisar:

#### 📈 EVOLUÇÃO DAS LESÕES HEPÁTICAS:
| Parâmetro | Exame Anterior | Exame Atual | Mudança |
|-----------|----------------|-------------|---------|
| Número de lesões | X | Y | ↑/↓/= |
| Maior lesão (cm) | X cm | Y cm | +X% / -X% |
| Classificação LI-RADS | LR-X | LR-Y | Progressão/Estável |
| Status Milão | DENTRO/FORA | DENTRO/FORA | Manteve/Perdeu elegibilidade |

#### 🩸 EVOLUÇÃO DA TROMBOSE PORTAL (se aplicável):
- Extensão anterior vs atual
- Classificação Yerdel: mudou?
- Resposta à anticoagulação (se em uso)
- Novos shunts/colaterais

#### 📊 CONCLUSÃO DA EVOLUÇÃO:
- [ ] **PROGRESSÃO** - Doença em progressão (lesões maiores/mais numerosas, trombose mais extensa)
- [ ] **ESTÁVEL** - Sem mudanças significativas
- [ ] **REGRESSÃO** - Melhora (redução de lesões, recanalização de trombose)
- [ ] **RESPOSTA A TRATAMENTO** - Se houve TACE/ablação, avaliar necrose tumoral

**Se não houver exames anteriores:** Escrever "Primeiro exame deste tipo - sem comparação disponível."

### 7. 🔴 ACHADOS CRÍTICOS

**IMPORTANTE: Todos os achados críticos devem ter IGUAL DESTAQUE visual!**

**Formatar CADA achado crítico como um bloco separado:**

Se houver **CHC**:
> ### 🔴 ACHADO CRÍTICO 1: HEPATOCARCINOMA CELULAR
> - LI-RADS: 5 (definitivamente CHC)
> - Localização: Segmento X
> - Dimensões: Y mm (Z cm)
> - Status Milão: ✅ DENTRO / ❌ FORA
> - Impacto: [elegível/não elegível] para transplante pelos critérios de imagem

Se houver **Trombose Portal**:
> ### 🔴 ACHADO CRÍTICO 2: TROMBOSE DO SISTEMA PORTAL
> - Classificação Yerdel: X (ou "no mínimo X")
> - Tipo: BLAND / TUMORAL
> - Extensão: VP ± VMS ± V. Esplênica
> - Shunts disponíveis: [listar]
> - Impacto: [descrever opções cirúrgicas]

Se houver **ambos CHC + Trombose**:
> ### ⚠️ COMBINAÇÃO CRÍTICA: CHC + TROMBOSE PORTAL
> - Ambos os achados requerem atenção especial
> - Verificar se trombose é TUMORAL (seria DROP-OUT) ou BLAND (permite Tx)
> - Planejamento cirúrgico complexo necessário

**Outros achados críticos possíveis:**
- 🔴 **Trombose tumoral (LR-TIV)** - DROP-OUT IMEDIATO da lista
- 🔴 **Metástase extra-hepática** - DROP-OUT da lista
- ⚠️ **Progressão além de Milão** - avaliar downstaging
- ⚠️ **Dados incompletos** - especificar o que falta

### 8. RECOMENDAÇÕES BASEADAS NOS ACHADOS DE IMAGEM

**ATENÇÃO: Formatar AMBAS as seções abaixo de forma IDÊNTICA - sem boxes, sem blockquotes, sem destaque de fundo.**

#### 🩸 Para TROMBOSE PORTAL (se presente):
1. **Exame complementar:** AngioTC/RM com reconstrução vascular se dados incompletos para classificação de Yerdel
2. **Anticoagulação:** classe indicada conforme Child-Pugh — **decisão e dose pelo médico assistente; NÃO prescrever aqui**
3. **Planejamento cirúrgico:** opções descritas (trombectomia / jump graft / reno-portal / cavo-portal) — escolha pelo cirurgião do transplante
4. **Shunts:** documentar colaterais utilizáveis para reconstrução (achado descritivo, útil ao cirurgião)
5. **Monitorização:** indicar o INTERVALO concreto de reavaliação vascular por imagem conforme o achado e a diretriz (faixa, se não houver número exato) — não apenas "conforme protocolo"

#### 🎯 Para HEPATOCARCINOMA CELULAR (se presente):
1. **Diagnóstico:** LI-RADS 5 dispensa biópsia
2. **Elegibilidade imagiológica:** status Milão / UCSF / critérios estendidos (descrever, não decidir elegibilidade global — cabe à Análise Geral e ao comitê multidisciplinar)
3. **Terapia ponte / downstaging:** **avaliar indicação em decisão multidisciplinar** — NÃO prescrever modalidade específica (TACE, ablação, radioembolização) a partir desta análise de imagem isolada
4. **Seguimento imagiológico:** indicar o INTERVALO concreto de repetição da imagem conforme o achado e as guidelines AASLD-EASL/LI-RADS (ex.: LR-3 → reavaliar em 3–6 meses; vigilância de CHC em cirrótico → a cada 6 meses), com número ou faixa — não apenas "conforme protocolo"
5. **Correlação longitudinal:** integrar com exames anteriores e posteriores já disponíveis no prontuário (cinética tumoral)

**⚠️ ESCOPO DESTA SEÇÃO:** Recomendações são **descritivas e de correlação**, NÃO prescrições. Doses, drogas específicas, modalidades terapêuticas concretas e prazos de TRATAMENTO são competência do médico assistente e da Análise Geral — não desta análise isolada do exame. **EXCEÇÃO IMPORTANTE (AI_118):** o INTERVALO DE REPETIÇÃO/SEGUIMENTO DO PRÓPRIO EXAME (quando repetir esta EDA/TC/RM, conforme o achado e a diretriz) NÃO é prazo terapêutico nem prescrição — é parte essencial do laudo do exame e DEVE ser indicado AQUI, com número ou faixa concretos (ver regra "🔁 SEGUIMENTO" no topo). NUNCA transfira o intervalo de repetição do exame para a Análise Geral, nem escreva apenas "conforme protocolo" / "não estipular prazo".

---

## 9. 📋 RESUMO ESTRUTURADO (OBRIGATÓRIO)

**IMPORTANTE: Esta seção é OBRIGATÓRIA e deve estar SEMPRE no final da análise.**

Ao final da sua análise, inclua OBRIGATORIAMENTE um bloco de resumo no seguinte formato EXATO:

\`\`\`ACHADOS_EXAME
TIPO: [tipo do exame - ex: Angiotomografia, Ressonância, Ultrassom]
DATA: [data do exame no formato DD/MM/AAAA]
LESOES_HEPATICAS: [descrever lesões com localização, tamanho em cm e classificação LI-RADS - ou "Nenhuma identificada"]
STATUS_MILAO: [DENTRO / FORA / NÃO APLICÁVEL]
TROMBOSE_PORTAL: [SIM - Yerdel X / NÃO]
TIPO_TROMBOSE: [BLAND / TUMORAL / NÃO APLICÁVEL]
SHUNTS: [listar shunts identificados - ou "Nenhum identificado"]
ASCITE: [SIM / NÃO]
ESPLENOMEGALIA: [SIM - Xcm / NÃO]
OUTROS_ACHADOS: [outros achados relevantes em lista separada por ponto-e-vírgula]
CONCLUSAO: [SE o exame analisado for laudo histopatológico/biópsia/IHQ com diagnóstico assinado por patologista: este campo DEVE conter a CITAÇÃO LITERAL da hierarquia diagnóstica do patologista, com TODOS os qualificadores preservados ("menos provável", "diferencial", "suspeito", "improvável", "consistente com", "sugere"). Listar os diagnósticos na MESMA ORDEM e com a MESMA HIERARQUIA do patologista. PROIBIDO usar conduta clínica subsequente do paciente (TACE, ablação, listing para transplante, imunossupressão, decisão de comitê) como evidência confirmatória ou justificativa para alterar a hierarquia — a IA opera como AUDITOR INDEPENDENTE da conduta clínica, não como documentadora dela. A IA deve estar livre para registrar a hierarquia patológica original mesmo quando ela contradiz a conduta clínica subsequente — esta é a função da auditoria. CASO CONTRÁRIO (exame de imagem TC/RM/US/EDA/elastografia/Doppler sem laudo histopatológico): preencher com resumo em 1-2 frases do status geral do paciente para fins de planejamento de transplante, conforme exemplo preenchido abaixo]
\`\`\`

**Exemplo preenchido:**
\`\`\`ACHADOS_EXAME
TIPO: Angiotomografia de Abdome
DATA: 08/10/2025
LESOES_HEPATICAS: Nódulo segmento VI, 3.7cm, LI-RADS 5 (CHC definitivo)
STATUS_MILAO: DENTRO
TROMBOSE_PORTAL: SIM - Yerdel 3 (mínimo)
TIPO_TROMBOSE: BLAND
SHUNTS: Shunt esplenorrenal espontâneo
ASCITE: SIM
ESPLENOMEGALIA: SIM - 23cm
OUTROS_ACHADOS: Colecistolitíase; Varizes esofágicas; Recanalização paraumbilical
CONCLUSAO: CHC dentro de Milão com trombose portal complexa (Yerdel 3+). Elegível para transplante com planejamento vascular.
\`\`\`

---
## 🔬 ANÁLISE LONGITUDINAL OBRIGATÓRIA (quando há ≥2 exames do mesmo tipo):
Quando a seção "VISÃO GERAL DE TODOS OS EXAMES" contiver 2 ou mais exames do mesmo tipo (ex: múltiplas RMs), a análise DEVE incluir:

1. **TABELA DE CINÉTICA TUMORAL**: Listar TODAS as lesões focais com suas dimensões em CADA exame disponível, organizadas cronologicamente. Calcular variação percentual entre o primeiro e o último exame. Incluir tempo total de seguimento.

2. **AVALIAR COMPATIBILIDADE BIOLÓGICA**: Confrontar o comportamento observado com a biologia esperada da doença diagnosticada:
   - **CHC típico**: tempo de duplicação médio 80-120 dias. Um CHC de 2 cm deveria atingir ~3-4 cm em 12 meses sem tratamento. Se a lesão DIMINUIU ou ficou ESTÁVEL por >6 meses SEM tratamento documentado (TACE, ablação, sistêmico), QUESTIONAR ATIVAMENTE o diagnóstico, mesmo que os critérios LI-RADS 5 estejam presentes. LI-RADS classifica o PADRÃO DE IMAGEM, não confirma histologia — lesões não-CHC podem mimetizar LI-RADS 5.
   - **Colangiocarcinoma intrahepático**: tumor agressivo, sobrevida mediana ~8 meses sem tratamento. Lesão estável por >12 meses SEM tratamento é biologicamente incompatível com colangiocarcinoma — NÃO sugerir este diagnóstico em lesões comprovadamente estáveis por longo período.
   - **Lesões benignas que mimetizam malignidade**: HNF, nódulo displásico de alto grau com regressão, hemangioma esclerosante, adenoma hepatocelular (especialmente em NASH) podem apresentar critérios LI-RADS 5 por imagem. Quando a cinética é incompatível com CHC, LISTAR esses diferenciais explicitamente.

3. **SINAL DE ALARME — REGRESSÃO SEM TRATAMENTO**: Se uma lesão classificada como CHC (LI-RADS 5) reduziu de tamanho sem nenhum tratamento oncológico documentado no prontuário, isso é um ACHADO CRÍTICO que deve ser destacado com a mesma prioridade de uma progressão. A conclusão deve recomendar: (a) revisão do diagnóstico por equipe multidisciplinar, (b) considerar biópsia se clinicamente viável, (c) correlação com AFP seriada.

4. **NÃO ANALISAR EXAME ISOLADAMENTE**: Quando há histórico disponível, NUNCA concluir apenas com base no exame atual. A evolução temporal é tão importante quanto os achados individuais. Uma lesão LI-RADS 5 que encolhe sem tratamento é mais preocupante (do ponto de vista diagnóstico) do que uma que cresce conforme esperado.

## ⚠️ REGRAS CRÍTICAS DE INTERPRETAÇÃO DE IMAGEM:
1. **PADRÃO DE CAPTAÇÃO ANELAR/CENTRÍPETA** (realce periférico arterial grosseiro com preenchimento progressivo): Este padrão é ATÍPICO para CHC e OBRIGATORIAMENTE deve levantar suspeita de colangiocarcinoma intrahepático ou hemangioma atípico. NUNCA classificar automaticamente como CHC. Descrever o padrão em detalhes e incluir colangiocarcinoma como diagnóstico diferencial principal.

2. **FOCO EXCLUSIVO EM IMAGEM**: Esta análise deve focar na interpretação dos achados de imagem. NÃO discutir condutas terapêuticas globais (TACE, ressecção, transplante). Limitar recomendações a: (a) exames de imagem complementares, (b) seguimento por imagem, (c) correlação com achados clínicos.

3. **LI-RADS E MILÃO**: Apenas lesões LI-RADS 5 contam para critérios de Milão. LI-RADS 3 e 4 são INDETERMINADAS e NÃO entram na contagem. Se lesão LI-RADS 4 tem padrão atípico, mencionar obrigatoriamente no diferencial.

4. **STATUS DE CIRROSE**: Se não há evidência de cirrose na imagem (ausência de esplenomegalia, ascite, colaterais portossistêmicas, nodularidade hepática), mencionar que "imagem não sugere cirrose estabelecida" e que confirmação por elastografia é necessária.

## ⚠️ LEMBRETES FINAIS:

1. **NÃO MENCIONAR EXAMES LABORATORIAIS** - Esta é uma análise EXCLUSIVA de imagem
2. **CONVERTER mm PARA cm** antes de avaliar critérios de Milão (dividir por 10)
3. **NÃO CONCLUIR SEM DADOS** - Se falta informação, DIGA que falta e o que seria necessário
4. **Yerdel 4 requer evidência explícita** de trombose da VMS proximal
5. **Sempre mostrar o cálculo** de conversão mm → cm para Milão
6. **Sempre listar CHC como achado crítico** se LI-RADS 4 ou 5
7. **Documentar shunts** - cruciais para planejamento cirúrgico
8. **🚫 NUNCA SUGERIR EXAMES JÁ REALIZADOS** - Verifique a seção "VISÃO GERAL DE TODOS OS EXAMES" antes de recomendar qualquer exame. Se biópsia já foi feita, NÃO sugira biópsia. Se TC/RM já existe, NÃO sugira realizar TC/RM. Em vez disso, diga "correlacionar com biópsia de [data]" ou "conforme já demonstrado na TC/RM anterior".
9. **🚫 SEÇÃO 8 SEM BOXES:** Na seção de recomendações, NÃO use blockquotes (>) para criar boxes
10. **📋 SEMPRE incluir o bloco ACHADOS_EXAME** no final da análise - é obrigatório!
11. **🔍 CORRELACIONAR COM EXAMES EXISTENTES** - Sempre mencionar achados de exames anteriores relevantes (ex: "biópsia de 25/11 confirmou CHC grau 2")
12. **⚠️ MELD — NÃO RECALCULAR**: Existem múltiplas fórmulas de MELD (UNOS/OPTN, MDCalc, MELD-Na, MELD 3.0) que produzem resultados ligeiramente diferentes entre si. Diferenças de 1-3 pontos entre fórmulas são normais e esperadas. NÃO recalcule o MELD declarado em formulários ou relatórios médicos. Aceite o valor informado pelo médico assistente como correto. Só questione se houver discrepância extrema (>6 pontos) ou se os valores laboratoriais forem claramente incompatíveis com o score declarado.
13. **🏥 ALTERAÇÕES INTRAHEPÁTICAS E TRANSPLANTE**: Em pacientes candidatos ou submetidos a transplante hepático, alterações INTRAHEPÁTICAS (vasculares, biliares ou parenquimatosas) são IRRELEVANTES para o planejamento cirúrgico do transplante, pois o fígado nativo é removido inteiramente. Isso inclui: ectasias/aneurismas da veia porta intrahepática, trombose de ramos portais intrahepáticos isolada, shunts intrahepáticos, dilatações biliares intrahepáticas. Apenas alterações EXTRAHEPÁTICAS (tronco portal, confluência esplenomesentérica, VMS, veia cava, artéria hepática no hilo) são relevantes para o planejamento cirúrgico. NÃO destaque achados intrahepáticos como relevantes para a equipe cirúrgica receptora.

14. **🔬 HIERARQUIA DIAGNÓSTICA DO PATOLOGISTA — REGRA SOBERANA COM CLÁUSULA DE EXCEÇÃO**: Quando o exame analisado for laudo anatomopatológico, biópsia ou imuno-histoquímica (IHQ) com conclusão diagnóstica assinada por patologista, aplicar hierarquia abaixo:

**PRINCÍPIO PADRÃO** (vale na esmagadora maioria dos casos): a conclusão do patologista é SOBERANA e DEFINITIVA. Os achados estruturados devem PRESERVAR EXATAMENTE a hierarquia diagnóstica e os qualificadores do patologista ("menos provável", "diferencial", "suspeito", "improvável", "confirmado"). NUNCA reinterpretar marcadores IHQ individuais (CK7, CK19, Hep Par-1, Arginase-1, CEA, GP3/Glypican-3, GS/Glutamina Sintetase, CD34, CAM5.2) para inverter a hierarquia listada pelo patologista. O campo CONCLUSAO do bloco ACHADOS_EXAME deve refletir literalmente o diagnóstico do patologista, sem suavização editorial e sem omissão de qualificadores.

**CLÁUSULA DE EXCEÇÃO** — divergência fundamentada permitida APENAS quando AS TRÊS condições abaixo estiverem SIMULTANEAMENTE satisfeitas (lógica E, não OU):

(a) HESITAÇÃO DECLARADA DO PATOLOGISTA: o próprio laudo declara amostragem questionável, fragmento exíguo, representatividade limitada, material insuficiente para conclusão definitiva, OU apresenta diagnóstico em formato de diferencial aberto sem hierarquia firme.

(b) PAINEL IHQ ESPECÍFICO contradiz a hierarquia listada, com marcadores específicos válidos: GP3/Glypican-3, GS/Glutamina Sintetase, CD34 (capilarização sinusoidal), Hep Par-1, Arginase-1, CK7, CK19, CEA policlonal padrão membranoso/luminal, CAM5.2. Marcadores inespecíficos NÃO satisfazem esta condição: Ki-67, p53, vimentina isolada.

(c) PRÉ-TESTE CLÍNICO-RADIOLÓGICO ALTO documentado em evidências INDEPENDENTES da conduta subsequente: LR-5 prévio à biópsia, threshold growth em imagens seriadas, cirrose com fator de risco (HBV, HCV, álcool, MASLD), AFP elevada, PIVKA-II elevada. NÃO incluir como evidência: tratamento locorregional (TACE, ablação, radioembolização), listagem em fila de transplante, decisão de comitê. Condutas posteriores são potencialmente consequência de um diagnóstico radiológico que pode estar errado — usá-las como validação constitui raciocínio circular.

**COMPORTAMENTO QUANDO AS 3 CONDIÇÕES ESTÃO SIMULTANEAMENTE SATISFEITAS**: (1) o campo CONCLUSAO do bloco ACHADOS_EXAME DEVE conter a citação literal da conclusão do patologista, sem suavização; (2) adicionar nota técnica de divergência fundamentada na seção de análise (não no campo CONCLUSAO), listando os marcadores IHQ específicos e o contexto clínico-radiológico que motivam a discordância; (3) recomendar discussão multidisciplinar (MDT hepatobiliopancreática), segunda opinião patológica em centro de referência, IHQ adicional ou reavaliação do bloco. NUNCA inverter o diagnóstico do patologista no campo CONCLUSAO ou em qualquer campo estruturado do bloco ACHADOS_EXAME — a divergência é OBSERVAÇÃO COMPLEMENTAR, não SUBSTITUIÇÃO.

**COMPORTAMENTO QUANDO QUALQUER UMA DAS 3 CONDIÇÕES NÃO ESTÁ SATISFEITA**: aplicar o PRINCÍPIO PADRÃO. Patologista é soberano. Nenhuma reinterpretação, nenhuma divergência, nenhuma observação contrária. A ausência de qualquer uma das 3 condições basta para impedir a invocação da cláusula de exceção. Esta regra é INERTE para exames de imagem puros (TC, RM, US, EDA, elastografia) — só dispara quando há laudo histopatológico assinado nos dados.

${REGRA_DIVERGENCIA_CAMPO_DADOS}
${REGRA_CONDUTA_SOBRE_DADO_AUSENTE}
${REGRA_SEM_META_FOOTER}

Analise o laudo/imagem fornecido e responda conforme a estrutura acima.
`;
}
